import {
  Content,
  Header,
  HeaderLabel,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  TableColumn,
  Table,
  Breadcrumbs,
} from '@backstage/core-components';
import { BadResponse } from './BadResponse';
import { Grid, Typography } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { PDASchema, drEventTaxonomyReferenceDataProxyApiRef } from '../../api';
import { flattenSchema, buildGlobalsData, PLATFORM_MAP } from '../../util';
import useAsync from 'react-use/lib/useAsync';
import { useParams } from 'react-router-dom';

type DenseTableProps = {
  props: PDASchema;
};

export const DenseTable = ({ props }: DenseTableProps) => {
  const columns: TableColumn[] = [
    { title: 'Parameter Name', field: 'paramName' },
    { title: 'Description', field: 'description' },
  ];

  const flattenedSchema = flattenSchema(props.schema);
  const globalsData = buildGlobalsData(flattenedSchema, props.datasetName);

  return (
    <Page themeId="tool">
      <Header
        title="Event Taxonomy Reference Data (ETRD)"
        subtitle="Streamline feature tracking with event taxonomy-related guidance"
      >
        <HeaderLabel label="Owner" value="Data Reliability" />
        <HeaderLabel label="Slack" value="#data-reliability" />
      </Header>
      <Content>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Grid container justifyContent="space-between" alignItems="center">
              <Breadcrumbs>
                <Link to="/dr-event-taxonomy-reference-data">Home</Link>
                <Link to="/dr-event-taxonomy-reference-data/globals">Globals</Link>
              </Breadcrumbs>
              <Typography variant="body2" color="textSecondary">
                NOTE: Legacy clickstream schemas may include additional segment globals, see{' '}
                <Link to="https://segment.com/docs/connections/spec/common/">here</Link> for more info.
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Table
              title={`${props.datasetName.replace('prism.clickstream.', '')} Global Parameters`}
              options={{ search: false, paging: false, sorting: true }}
              columns={columns}
              data={globalsData.sort((a, b) => (a.paramName > b.paramName ? 1 : -1))}
            />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export const GlobalsComponent = ({ mockData }: any) => {
  const { platformID } = useParams();
  const schemaName = PLATFORM_MAP[platformID as keyof typeof PLATFORM_MAP];
  const apiClient = useApi(drEventTaxonomyReferenceDataProxyApiRef);
  const { value, loading, error } = useAsync(async () => {
    if (mockData !== undefined) {
      return mockData.data;
    }

    return apiClient.get<PDASchema>(`prism-dataset-api/v1/datasets/${schemaName}`);
  }, []);

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  } else if (value instanceof Response) {
    return <BadResponse response={value} />;
  }

  return <DenseTable props={value || {}} />;
};
